/**
 * StreakBadge — header pill showing the user's current daily streak.
 *
 * Renders nothing when the streak is 0 (avoids guilt-tripping new
 * users). Pulses on the day the streak was kept; goes dim otherwise.
 *
 * Polled once on mount via GET /streak/me; LessonQuiz also calls
 * `onStreakUpdated()` after a quiz pass so the badge updates without
 * a refetch.
 */
import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";

let _cachedStreak = null;

export default function StreakBadge({ refreshSignal }) {
  const [streak, setStreak] = useState(_cachedStreak);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/streak/me");
        if (!cancelled) {
          _cachedStreak = data;
          setStreak(data);
        }
      } catch { /* silent — unauth or 404 */ }
    })();
    return () => { cancelled = true; };
  }, [refreshSignal]);

  if (!streak || !streak.current) return null;
  const kept = !!streak.kept_today;
  return (
    <div
      className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-display font-semibold border transition ${
        kept
          ? "bg-orange-500/15 border-orange-400/40 text-orange-200"
          : "bg-white/[0.04] border-white/10 text-white/55"
      }`}
      title={kept ? `${streak.current}-day streak (kept today)` : `${streak.current}-day streak — keep it alive by passing a quiz today`}
      data-testid="streak-badge"
      data-kept={kept ? "true" : "false"}
    >
      <Flame size={12} className={kept ? "animate-pulse" : ""} />
      {streak.current}
    </div>
  );
}
